import { useGrowthBook } from '@growthbook/growthbook-react'
import type React from 'react'
import { useEffect, useMemo, useState } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useEnableCampaignUplift } from '../../hooks/useEnableCampaignUplift'

// Coupons, Segments and Campaigns live under Marketing when
// `bs-enable-campaign-uplift` is on, and under Loyalty otherwise. Every link to
// them goes through here so it lands on the right side of that flag.

export type MarketingTab =
  | 'coupons'
  | 'segments'
  | 'campaigns'
  | 'cart-recovery'

export const MARKETING_TAB_IDS: MarketingTab[] = [
  'coupons',
  'segments',
  'campaigns',
  'cart-recovery',
]

// Loyalty's URL segment for each moved tab. Cart Recovery only exists under
// Marketing. Coupons is 'coupon-catalog' there because the legacy POS module
// owns /loyalty/coupons (see LoyaltyTabBar), which must never be redirected.
const LOYALTY_SEGMENT: Record<MarketingTab, string> = {
  coupons: 'coupon-catalog',
  segments: 'segments',
  campaigns: 'campaigns',
  'cart-recovery': 'overview',
}

// Where the builder returns to. Abandoned cart flows aren't on the Campaigns tab
// while Marketing is on.
export const builderBackTab = (
  campaignType: string | null,
  upliftEnabled: boolean,
): MarketingTab =>
  upliftEnabled && campaignType === 'abandoned_cart'
    ? 'cart-recovery'
    : 'campaigns'

export const marketingTabPath = (
  storeId: string | undefined,
  tab: MarketingTab,
  upliftEnabled: boolean,
): string =>
  upliftEnabled
    ? `/s/${storeId}/marketing/${tab}`
    : `/s/${storeId}/loyalty/${LOYALTY_SEGMENT[tab]}`

// A page below a tab, e.g. 'campaigns/builder' or 'segments/<id>'.
export const marketingSubPath = (
  storeId: string | undefined,
  subPath: string,
  upliftEnabled: boolean,
): string =>
  `/s/${storeId}/${upliftEnabled ? 'marketing' : 'loyalty'}/${subPath}`

export const useMarketingPaths = (storeId: string | undefined) => {
  const upliftEnabled = useEnableCampaignUplift()
  return useMemo(
    () => ({
      tab: (tab: MarketingTab) => marketingTabPath(storeId, tab, upliftEnabled),
      sub: (subPath: string) =>
        marketingSubPath(storeId, subPath, upliftEnabled),
    }),
    [storeId, upliftEnabled],
  )
}

const OLD_LOYALTY_PATH =
  /^(\/s\/[^/]+)\/loyalty\/(coupon-catalog|campaigns-flows|campaigns|segments)(\/.*)?$/
const MARKETING_PATH = /^(\/s\/[^/]+)\/marketing(?:\/([^/]+)(\/.*)?)?\/?$/

// Where `pathname` belongs for this flag state, or null when it's already right.
export const marketingRedirectTarget = (
  pathname: string,
  upliftEnabled: boolean,
): string | null => {
  if (upliftEnabled) {
    const match = OLD_LOYALTY_PATH.exec(pathname)
    if (!match) return null
    const [, store, segment, rest = ''] = match
    const tab =
      segment === 'coupon-catalog'
        ? 'coupons'
        : segment === 'campaigns-flows'
          ? 'campaigns'
          : segment
    return `${store}/marketing/${tab}${rest}`
  }
  const match = MARKETING_PATH.exec(pathname)
  if (!match) return null
  const [, store, segment, rest = ''] = match
  if (!segment) return `${store}/loyalty/overview`
  // The campaign report only exists under Marketing.
  if (rest.startsWith('/report')) return `${store}/loyalty/campaigns`
  const loyaltySegment = LOYALTY_SEGMENT[segment as MarketingTab] ?? segment
  return `${store}/loyalty/${loyaltySegment}${rest}`
}

// The layout sets the store's flag attributes in an effect, after this route's
// first render. Until then a store-targeted flag reads as off, which would send
// a deep link away. The timeout keeps a layout that never sets them from
// leaving the page blank.
const ATTRIBUTES_TIMEOUT_MS = 1500
const useFlagAttributesReady = (pathname: string): boolean => {
  const growthbook = useGrowthBook()
  const routeStoreId = /^\/s\/([^/]+)/.exec(pathname)?.[1]
  const ready =
    !growthbook ||
    !routeStoreId ||
    String(growthbook.getAttributes().storeId ?? '') === routeStoreId
  const [timedOut, setTimedOut] = useState(false)
  useEffect(() => {
    if (ready) return
    const timer = setTimeout(() => setTimedOut(true), ATTRIBUTES_TIMEOUT_MS)
    return () => clearTimeout(timer)
  }, [ready])
  return ready || timedOut
}

// Sends a page reached through the other side of the flag to its proper URL,
// before anything mounts or fetches. The query string and hash are kept.
export const MarketingPathGuard: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const upliftEnabled = useEnableCampaignUplift()
  const { pathname, search, hash } = useLocation()
  const attributesReady = useFlagAttributesReady(pathname)
  if (!attributesReady) return null
  const target = marketingRedirectTarget(pathname, upliftEnabled)
  if (target) return <Navigate to={`${target}${search}${hash}`} replace />
  return <>{children}</>
}
